import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Outlet, useOutletContext, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { getStages, getShooters, getScores } from '../../api';
import StageSelect from './scoring/StageSelect';
import ShooterSquadSelect from './scoring/ShooterSquadSelect';
import WelcomeScreen from './scoring/WelcomeScreen';
import EquipmentCheck from './scoring/EquipmentCheck';
import ShooterList from './scoring/ShooterList';
import ScoreEntry from './scoring/ScoreEntry';
import ScoreReview from './scoring/ScoreReview';
import ScoreSignature from './scoring/ScoreSignature';

function ScoringLayout({ context }) {
  return <Outlet context={context} />;
}

export default function Scoring() {
  const { matchId } = useParams();
  const { match, permissions } = useOutletContext();
  const { t } = useTranslation();

  const [stages, setStages] = useState([]);
  const [shooters, setShooters] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [speedTickets, setSpeedTickets] = useState({});

  // Persist pendingScore in localStorage so it survives refresh
  const pendingKey = `__scoring_pending_${matchId}`;
  const [pendingScore, setPendingScoreRaw] = useState(() => {
    try { return JSON.parse(localStorage.getItem(pendingKey)); } catch { return null; }
  });
  const setPendingScore = useCallback((val) => {
    setPendingScoreRaw(val);
    if (val) localStorage.setItem(pendingKey, JSON.stringify(val));
    else localStorage.removeItem(pendingKey);
  }, [pendingKey]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [shootersRes, scoresRes] = await Promise.all([
        getShooters(matchId).catch(() => ({ data: [] })),
        getScores(matchId).catch(() => ({ data: [] })),
      ]);
      setShooters(Array.isArray(shootersRes.data) ? shootersRes.data : []);
      const scoresData = scoresRes.data?.scores ?? scoresRes.data;
      setScores(Array.isArray(scoresData) ? scoresData : []);

      const stageIds = match.stages || [];
      if (stageIds.length > 0) {
        const { data: stagesData } = await getStages(stageIds.join(','));
        setStages(Array.isArray(stagesData) ? stagesData : [stagesData]);
      }
    } catch (err) {
      console.error('Failed to load scoring data', err);
    } finally {
      setLoading(false);
    }
  }, [matchId, match]);

  useEffect(() => { loadData(); }, [loadData]);

  const refreshScores = useCallback(async () => {
    try {
      const { data } = await getScores(matchId);
      const scoresData = data?.scores ?? data;
      setScores(Array.isArray(scoresData) ? scoresData : []);
    } catch {}
  }, [matchId]);

  const refreshShooters = useCallback(async () => {
    try {
      const { data } = await getShooters(matchId);
      setShooters(Array.isArray(data) ? data : []);
    } catch {}
  }, [matchId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (stages.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>{t('scoring.noStages1')}</Typography>
        <Typography variant="body1" color="text.secondary">{t('scoring.noStages2')}</Typography>
      </Container>
    );
  }

  const addSpeedTicket = (stageIdx, shooterId) => {
    setSpeedTickets((prev) => {
      const key = String(stageIdx);
      const existing = prev[key] || [];
      if (existing.includes(shooterId)) return prev;
      return { ...prev, [key]: [...existing, shooterId] };
    });
  };

  const canEditScores = permissions?.role === 'owner' || permissions?.permissions?.includes('approve-rescoring') || permissions?.permissions?.includes('manage');
  const context = { matchId, match, stages, shooters, scores, refreshScores, refreshShooters, speedTickets, addSpeedTicket, pendingScore, setPendingScore, canEditScores };

  return (
    <Routes>
      <Route element={<ScoringLayout context={context} />}>
        <Route index element={<StageSelect />} />
        <Route path="stage/:stageIdx" element={<ShooterSquadSelect />} />
        <Route path="stage/:stageIdx/squad/:squadId" element={<WelcomeScreen />} />
        <Route path="stage/:stageIdx/squad/:squadId/welcome-mode" element={<EquipmentCheck />} />
        <Route path="stage/:stageIdx/squad/:squadId/score-mode" element={<ShooterList />} />
        <Route path="stage/:stageIdx/squad/:squadId/score-mode/scoring/:shooterId" element={<ScoreEntry />} />
        <Route path="stage/:stageIdx/squad/:squadId/score-mode/scoring/:shooterId/signature" element={<ScoreSignature />} />
        <Route path="stage/:stageIdx/squad/:squadId/score-mode/reviewing/:shooterId" element={<ScoreReview />} />
      </Route>
    </Routes>
  );
}
